import { DateTime } from 'luxon';
import { create } from 'zustand';

import type { BannerDetails, BannerVisibilityToken } from 'models/banner';
import type { GetManualBannerDetailsResponse } from 'models/reqres';
import { normalizeManualBanners, withConfiguredBannerFallback } from 'utils/banner';
import config from 'utils/config';
import { ReadJSON } from 'utils/s3';
import { getTradingHours, NEW_YORK_TIMEZONE } from 'utils/time';

type MaintenanceMode = 'on' | 'off' | 'notice';
type BannerGet = () => BannerState;

const isNotFoundError = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && 'status' in error && error.status === 404;

const INITIAL_STATE = {
    queryExchangeDown: false,
    manualBanners: [] as BannerDetails[],
    manualBannersInitialized: false,
    automatedBannerVisibilityTokens: config.automatedBannerVisibilityTokens,
    maintenanceMode: 'off' as MaintenanceMode,
    maintenanceStart: undefined as DateTime | undefined,
    maintenanceEnd: undefined as DateTime | undefined,
    currentlyInTradingHours: true,
    tradingHoursStart: undefined as DateTime | undefined,
    tradingHoursEnd: undefined as DateTime | undefined,
};

interface BannerActions {
    initialize: (initialManualBanners?: BannerDetails[]) => void;
    fetchManualBanners: () => void;
    updateTradingHours: () => void;
    dispose: () => void;
    queryExchangeWentDown: () => void;
    queryExchangeRecovered: () => void;
}

export interface BannerState {
    queryExchangeDown: boolean;
    manualBanners: BannerDetails[];
    manualBannersInitialized: boolean;
    automatedBannerVisibilityTokens: BannerVisibilityToken[];
    maintenanceMode: MaintenanceMode;
    maintenanceStart: DateTime | undefined;
    maintenanceEnd: DateTime | undefined;
    currentlyInTradingHours: boolean;
    tradingHoursStart: DateTime | undefined;
    tradingHoursEnd: DateTime | undefined;
    actions: BannerActions;
}

function updateTradingHours(get: BannerGet): Partial<BannerState> {
    const tradingHours = getTradingHours();
    const currentState = get();

    const start = tradingHours?.start ?? currentState.tradingHoursStart;
    const end = tradingHours?.end ?? currentState.tradingHoursEnd;

    const updates: Partial<BannerState> = {};
    if (tradingHours?.start) updates.tradingHoursStart = tradingHours.start;
    if (tradingHours?.end) updates.tradingHoursEnd = tradingHours.end;

    if (tradingHours === undefined) {
        // No trading-hour windows are configured for today, so the market is closed.
        // Fail closed here rather than letting `currentlyInTradingHours` retain its
        // stale/initial `true` value (which would let users trade on a no-window day).
        updates.currentlyInTradingHours = false;
    } else if (start !== undefined && end !== undefined) {
        const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
        updates.currentlyInTradingHours = start <= now && now <= end;
    }

    return updates;
}

export const useBannerStore = create<BannerState>()((set, get) => ({
    ...INITIAL_STATE,

    actions: {
        initialize: (initialManualBanners = []) => {
            // One `set`, not a seed followed by a fallback `set`: the second write re-rendered
            // every subscriber for a value the first could already have carried.
            set({
                ...INITIAL_STATE,
                manualBanners: withConfiguredBannerFallback(initialManualBanners),
                manualBannersInitialized: true,
            });
        },

        fetchManualBanners: () => {
            const url = `${config.kalshiProductInfoCloudfrontUrl}/manual-banner-details.json?v=${Date.now()}`;
            ReadJSON<GetManualBannerDetailsResponse>(url, (error) => {
                if (isNotFoundError(error)) {
                    set({ manualBanners: withConfiguredBannerFallback([]), manualBannersInitialized: true });
                }
            }).then((data) => {
                if (!data) return;

                // Mock data for testing
                // const content = 'Maintainance in progress. We expect to be back by 5:00 PM UTC.';
                // const visibilityTokens = ['landing'] as BannerVisibilityToken[];
                // const position = 'top';
                // const banners = [] as BannerDetails[];
                // const banners = [
                //     {
                //         content: 'Maintainance in progress. We expect to be back by 5:00 PM UTC.',
                //         visibilityTokens: ['landing'] as BannerVisibilityToken[],
                //         position: 'top',
                //         variant: 'notice' as BannerVariant,
                //         platforms: ['web'] as BannerPlatform[],
                //     },
                //     {
                //         content: 'Transfers page banner only',
                //         visibilityTokens: ['transfers'] as BannerVisibilityToken[],
                //         position: 'top',
                //         variant: 'severe' as BannerVariant,
                //         platforms: ['web'] as BannerPlatform[],
                //     },
                // ];

                set({
                    manualBanners: withConfiguredBannerFallback(normalizeManualBanners(data)),
                    manualBannersInitialized: true,
                });
            });
        },

        updateTradingHours: () => {
            const updates = updateTradingHours(get);
            set(updates);
        },

        dispose: () => {
            set(INITIAL_STATE);
        },

        queryExchangeWentDown: () => set({ queryExchangeDown: true }),
        queryExchangeRecovered: () => set({ queryExchangeDown: false }),
    },
}));
